import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { formatApiErrorDetail } from "@/lib/api";
import {
  ArrowRight,
  ShieldCheck,
  ChartLineUp,
  UsersThree,
  LockKey,
  Envelope,
  Globe,
} from "@phosphor-icons/react";
import NusacertLogo from "@/components/NusacertLogo";

export default function LoginPage() {
  const { login, user } = useAuth();
  const { t, lang, toggle } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-12 bg-[#F5F7F9]">
      {/* Left Brand Showcase Panel (5 cols) */}
      <div className="hidden lg:flex lg:col-span-5 flex-col justify-between p-12 bg-gradient-to-br from-[#173B4D] via-[#1C4E63] to-[#24566D] text-white relative overflow-hidden">
        {/* Brand Lockup */}
        <div className="bg-white/95 rounded-xl px-4 py-2.5 shadow-sm inline-flex items-center self-start">
          <img src="/nusacert-logo.png" alt="Nusacert — Professional Service for Audit & Certification" className="h-10 w-auto object-contain" />
        </div>

        {/* Brand Proposition */}
        <div className="max-w-md my-auto">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D9A321] bg-black/20 px-3 py-1 rounded-md mb-4">
            <ShieldCheck size={16} weight="fill" />
            <span>Certifying Excellence, Ensuring Trust</span>
          </div>

          <h1 className="text-4xl font-bold tracking-tight text-white leading-tight">
            Akselerasi Pipeline Penjualan Sertifikasi ISO.
          </h1>

          <p className="mt-4 text-sm text-slate-200 leading-relaxed">
            Platform CRM terpadu untuk tim sales Nusacert — pantau pipeline audit, follow-up prospek, jadwal meeting, dan analisis revenue secara real-time.
          </p>

          {/* Metric Highlights */}
          <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-white/15">
            <div>
              <div className="font-mono text-2xl font-bold text-white">6</div>
              <div className="text-[11px] text-slate-300 uppercase tracking-wider font-semibold mt-0.5">
                Tahap Pipeline
              </div>
            </div>
            <div>
              <div className="font-mono text-2xl font-bold text-[#D9A321]">3</div>
              <div className="text-[11px] text-slate-300 uppercase tracking-wider font-semibold mt-0.5">
                Tingkat Akses
              </div>
            </div>
            <div>
              <div className="font-mono text-2xl font-bold text-[#2BB8CA]">100%</div>
              <div className="text-[11px] text-slate-300 uppercase tracking-wider font-semibold mt-0.5">
                Data Real-Time
              </div>
            </div>
          </div>
        </div>

        {/* Footer info */}
        <div className="text-xs text-slate-300 flex items-center justify-between">
          <span>&copy; {new Date().getFullYear()} Nusacert</span>
          <span className="font-mono text-[11px] opacity-75">v2.0 · B2B Edition</span>
        </div>
      </div>

      {/* Right Authentication Form Panel (7 cols) */}
      <div className="lg:col-span-7 flex items-center justify-center p-6 sm:p-12 relative">
        {/* Language Switcher */}
        <button
          onClick={toggle}
          data-testid="login-lang-toggle"
          className="absolute top-6 right-6 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E2E8F0] bg-white text-xs font-semibold text-[#64748B] hover:text-[#173042] shadow-xs transition-colors"
        >
          <Globe size={14} />
          <span>
            <span className={lang === "id" ? "text-[#173B4D] font-bold underline underline-offset-4" : "opacity-50"}>
              ID
            </span>
            <span className="mx-1 opacity-30">/</span>
            <span className={lang === "en" ? "text-[#173B4D] font-bold underline underline-offset-4" : "opacity-50"}>
              EN
            </span>
          </span>
        </button>

        <div className="w-full max-w-md">
          {/* Brand Header */}
          <div className="mb-8">
            <img
              src="/nusacert-logo.png"
              alt="Nusacert"
              className="h-12 w-auto object-contain mb-4"
            />
            <div className="text-xs font-bold uppercase tracking-wider text-[#0F7180]">
              {t("auth.welcome")}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#173042] mt-1">
              {t("auth.signin")}
            </h2>
            <p className="text-sm text-[#64748B] mt-1.5">
              {t("auth.signInDesc")}
            </p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5">
                {t("auth.email")}
              </label>
              <div className="relative">
                <Envelope
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                  className="w-full rounded-lg border border-[#CBD5E1] bg-white pl-10 pr-3.5 py-2.5 text-sm text-[#173042] placeholder:text-slate-400 focus:border-[#173B4D] focus:outline-none focus:ring-1 focus:ring-[#173B4D] transition-colors"
                  placeholder="name@nusacert.co.id"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5">
                {t("auth.password")}
              </label>
              <div className="relative">
                <LockKey
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                  className="w-full rounded-lg border border-[#CBD5E1] bg-white pl-10 pr-3.5 py-2.5 text-sm text-[#173042] placeholder:text-slate-400 focus:border-[#173B4D] focus:outline-none focus:ring-1 focus:ring-[#173B4D] transition-colors"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs font-medium text-red-700"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-[#173B4D] hover:bg-[#112B38] text-white py-3 px-4 rounded-lg text-sm font-semibold flex items-center justify-between shadow-xs transition-colors disabled:opacity-60"
            >
              <span>{loading ? "Memproses..." : t("auth.login")}</span>
              <ArrowRight size={18} weight="bold" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
